import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { GalleryConfig, GalleryConfigContextType } from '../types/gallery';
import { galleryApiService } from '../services/galleryApiService';

interface ConfigState {
  config: GalleryConfig | null;
  loading: boolean;
  error: string | null;
}

type ConfigAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CONFIG'; payload: GalleryConfig }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

const initialState: ConfigState = {
  config: null,
  loading: false,
  error: null,
};

function configReducer(state: ConfigState, action: ConfigAction): ConfigState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_CONFIG':
      return { ...state, config: action.payload, loading: false, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

const GalleryConfigContext = createContext<GalleryConfigContextType | undefined>(undefined);

interface GalleryConfigProviderProps {
  children: ReactNode;
}

export const GalleryConfigProvider: React.FC<GalleryConfigProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(configReducer, initialState);

  const refreshConfig = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      // Get domain from current location
      const hostname = window.location.hostname;
      const config = await galleryApiService.getGalleryConfig(hostname);

      dispatch({ type: 'SET_CONFIG', payload: config });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load gallery configuration',
      });
    }
  };

  useEffect(() => {
    refreshConfig();
  }, []);

  const value: GalleryConfigContextType = {
    config: state.config,
    loading: state.loading,
    error: state.error,
    refreshConfig,
  };

  return <GalleryConfigContext.Provider value={value}>{children}</GalleryConfigContext.Provider>;
};

export const useGalleryConfig = (): GalleryConfigContextType => {
  const context = useContext(GalleryConfigContext);
  if (context === undefined) {
    throw new Error('useGalleryConfig must be used within a GalleryConfigProvider');
  }
  return context;
};
